import { Bot, Cog, History, User, Zap } from 'lucide-react';
import { useTranslation } from '../lib/i18n.js';
import { useApiQuery } from '../lib/api.js';
import { QueryState, TimeAgo } from './common.js';

const label = (type: string) =>
  type
    .split('.')
    .slice(1)
    .join(' ')
    .replace(/_/g, ' ');

/** Permitted history built from durable domain events (payloads are summaries, never bodies). */
export function Timeline({ id, kind }: { id: string; kind: 'work' }) {
  const { t } = useTranslation();
  const query = useApiQuery('getWorkTimeline', { params: { id }, query: { limit: '50' } }, { enabled: kind === 'work' });
  return (
    <QueryState query={query} empty={query.data?.data.length === 0 ? <p className="text-sm text-fg-subtle">{t('work.detail.timelineEmpty')}</p> : undefined}>
      <ol className="relative flex flex-col gap-3 border-l border-border pl-4">
        {(query.data?.data ?? []).map((e) => {
          const summary = e.summary as Record<string, unknown>;
          // Changes made by an automation rule name the rule instead of "system".
          const automation = summary.automation && typeof summary.automation === 'object' ? (summary.automation as { ruleName?: string }) : null;
          const Icon = automation ? Zap : e.actorKind === 'AGENT' ? Bot : e.actorKind === 'USER' ? User : Cog;
          return (
            <li key={e.id} className="relative text-sm">
              <span className="absolute top-0.5 -left-[25px] flex size-4 items-center justify-center rounded-full border border-border bg-surface">
                <Icon className="size-2.5 text-fg-subtle" />
              </span>
              <div className="flex flex-wrap items-center gap-x-2">
                <span className="font-medium capitalize">{label(e.type)}</span>
                {typeof summary.from === 'string' && typeof summary.to === 'string' ? (
                  <span className="text-fg-muted">
                    {summary.from} → {summary.to}
                  </span>
                ) : null}
                {Array.isArray(summary.fields) ? <span className="text-xs text-fg-subtle">{(summary.fields as string[]).join(', ')}</span> : null}
                {summary.historical ? <span className="text-xs text-fg-subtle">(Jira)</span> : null}
              </div>
              {e.type === 'work.automation_notice' && typeof summary.message === 'string' ? <p className="text-xs text-fg-muted">“{summary.message}”</p> : null}
              <div className="text-xs text-fg-subtle">
                {automation ? t('automations.byRule', { name: automation.ruleName ?? '' }) : (e.actor?.displayName ?? e.actorKind.toLowerCase())} · {String(summary.client ?? e.source).toLowerCase()} · <TimeAgo iso={e.occurredAt} />
              </div>
            </li>
          );
        })}
      </ol>
      <History className="hidden" />
    </QueryState>
  );
}
